import { useDateRange } from "@/dateRanges/DateRangeContext";
import { LAST_7_DAYS, DateRange } from "@/dateRanges/dateRanges";
import { ONE_DAY } from "@/dateRanges/windowSizes";
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, subDays } from "date-fns";
import { cn } from "@/lib/utils";
import { DateRange as DateRangeType } from "react-day-picker";

const LAST_3_DAYS: DateRange = {
  label: "Last 3 days",
  value: {
    from: () => subDays(new Date(), 3),
    to: () => new Date(),
    live: true,
  },
};

const LAST_14_DAYS: DateRange = {
  label: "Last 14 days",
  value: {
    from: () => subDays(new Date(), 14),
    to: () => new Date(),
    live: true,
  },
};

export const SIMPLE_DATE_RANGE_OPTIONS = {
  "last_3_days": {
    dateRange: LAST_3_DAYS,
    windowSize: ONE_DAY,
    windowSizeReadable: "day"
  },
  "last_7_days": {
    dateRange: LAST_7_DAYS,
    windowSize: ONE_DAY,
    windowSizeReadable: "day"
  },
  "last_14_days": {
    dateRange: LAST_14_DAYS,
    windowSize: ONE_DAY,
    windowSizeReadable: "day"
  },
  "custom": {
    dateRange: null,
    windowSize: ONE_DAY,
    windowSizeReadable: "day"
  }
}

export const useSimpleDateRangeOption = () => {
  const { dateRange, windowSize } = useDateRange();

  const selectedOptionKey = Object.keys(SIMPLE_DATE_RANGE_OPTIONS).find(
    (option) => {
      const optionConfig = SIMPLE_DATE_RANGE_OPTIONS[option];
      if (option === "custom") {
        // Check if it's a custom range (not matching any preset)
        return !Object.keys(SIMPLE_DATE_RANGE_OPTIONS).some(key => 
          key !== "custom" && 
          SIMPLE_DATE_RANGE_OPTIONS[key].dateRange?.label === dateRange?.label
        );
      }
      return optionConfig.dateRange?.label === dateRange?.label &&
        optionConfig.windowSize === windowSize;
    }
  ) || "last_7_days"; // Default to 7 days

  return { selectedOptionKey, selectedOption: SIMPLE_DATE_RANGE_OPTIONS[selectedOptionKey] }
}

export const SimpleDateRangeSelector = () => {
  const { dateRange, setDateRange, setWindowSize } = useDateRange();
  const { selectedOptionKey } = useSimpleDateRangeOption();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [customRange, setCustomRange] = useState<DateRangeType | undefined>(undefined);

  const handlePresetClick = (days: number) => {
    if (days === 3) {
      setDateRange(SIMPLE_DATE_RANGE_OPTIONS["last_3_days"].dateRange);
      setWindowSize(SIMPLE_DATE_RANGE_OPTIONS["last_3_days"].windowSize);
    } else if (days === 7) {
      setDateRange(SIMPLE_DATE_RANGE_OPTIONS["last_7_days"].dateRange);
      setWindowSize(SIMPLE_DATE_RANGE_OPTIONS["last_7_days"].windowSize);
    } else if (days === 14) {
      setDateRange(SIMPLE_DATE_RANGE_OPTIONS["last_14_days"].dateRange);
      setWindowSize(SIMPLE_DATE_RANGE_OPTIONS["last_14_days"].windowSize);
    }
    setPopoverOpen(false);
  };

  const handleCalendarSelect = (range: DateRangeType | undefined) => {
    setCustomRange(range);
    if (range?.from && range?.to) {
      const customDateRange: DateRange = {
        label: `${format(range.from, "MMM d")} - ${format(range.to, "MMM d, yyyy")}`,
        value: {
          from: () => range.from!,
          to: () => range.to!,
          live: false,
        }
      };
      setDateRange(customDateRange);
      setWindowSize(ONE_DAY);
      setPopoverOpen(false);
    }
  };

  const displayValue = selectedOptionKey === "custom" && dateRange
    ? dateRange.label
    : selectedOptionKey
      ? SIMPLE_DATE_RANGE_OPTIONS[selectedOptionKey].dateRange?.label
      : "Last 7 days";

  const currentDateRange = dateRange?.value
    ? {
        from: dateRange.value.from(),
        to: dateRange.value.to()
      }
    : undefined;

  const isPresetSelected = (days: number) => {
    if (days === 3) return selectedOptionKey === "last_3_days";
    if (days === 7) return selectedOptionKey === "last_7_days" || !selectedOptionKey;
    if (days === 14) return selectedOptionKey === "last_14_days";
    return false;
  };

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={popoverOpen}
          className={cn(
            "whitespace-nowrap inline-flex items-center text-sm transition-colors bg-white text-left font-normal px-2 rounded-md border border-input hover:bg-accent hover:text-accent-foreground cursor-pointer",
            !dateRange && "text-muted-foreground"
          )}
        >
          {displayValue}
          <CalendarIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3 border-b">
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={isPresetSelected(3) ? "default" : "outline"}
              size="sm"
              onClick={() => handlePresetClick(3)}
              className="text-xs"
            >
              3 Days
            </Button>
            <Button
              variant={isPresetSelected(7) ? "default" : "outline"}
              size="sm"
              onClick={() => handlePresetClick(7)}
              className="text-xs"
            >
              7 Days
            </Button>
            <Button
              variant={isPresetSelected(14) ? "default" : "outline"}
              size="sm"
              onClick={() => handlePresetClick(14)}
              className="text-xs"
            >
              14 Days
            </Button>
          </div>
        </div>
        <Calendar
          mode="range"
          defaultMonth={currentDateRange?.from}
          selected={customRange || currentDateRange}
          onSelect={handleCalendarSelect}
          numberOfMonths={2}
        />
      </PopoverContent>
    </Popover>
  );
};